import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Globe, ChevronDown } from "lucide-react";
import { useI18n, LANGS } from "../i18n";
import { CmsImage } from "../lib/images";

/**
 * Sub-services listed in the "Servicios" dropdown.
 * Slugs must match keys in `data/serviceDetails.js`.
 */
const SERVICE_ITEMS = [
  { slug: "xega-vip",           es: "Xega VIP",                                     en: "Xega VIP",                        pt: "Xega VIP" },
  { slug: "merch-finder",       es: "Merch Finder",                                 en: "Merch Finder",                    pt: "Merch Finder" },
  { slug: "e-tracker",          es: "E-Tracker",                                    en: "E-Tracker",                       pt: "E-Tracker" },
  { slug: "sentinel",           es: "Sentinel",                                     en: "Sentinel",                        pt: "Sentinel" },
  { slug: "escoltas",           es: "Escoltas",                                     en: "Executive Escort",                pt: "Escoltas" },
  { slug: "seguridad-privada",  es: "Seguridad Privada",                            en: "Private Security",                pt: "Segurança Privada" },
  { slug: "adultos-mayores",    es: "Localización Satelital Adultos Mayores",       en: "Satellite Location for Seniors",  pt: "Localização Satelital para Idosos" },
];

/**
 * Small hook that returns a boolean state + toggle/close helpers and
 * closes the panel when the user clicks outside of the anchor element.
 */
function useOutsideToggle() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);
  return { open, setOpen, ref };
}

export default function NavBar() {
  const { t, lang, setLang, pick } = useI18n();
  const nextLang = LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length];

  const desktopSubmenu = useOutsideToggle();  // hover + click
  const mobileSubmenu  = useOutsideToggle();  // tap-to-toggle

  const NAV_LINKS = [
    { href: "/quienes-somos",     label: t("nav.about"),      testid: "about" },
    { href: "/servicios",         label: t("nav.solutions"),  testid: "services", hasSubmenu: true },
    { href: "/centro-monitoreo",  label: t("nav.monitoring"), testid: "monitoring" },
    { href: "/contacto",          label: t("nav.contact"),    testid: "contact" },
  ];

  const submenuLinkClass =
    "flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-tech text-white/90 hover:text-cyan-300 hover:bg-cyan-400/10 transition rounded-md";

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-10 mt-3 sm:mt-4 relative">
        <div className="glass-card chrome-border px-3 sm:px-5 py-2 sm:py-3" data-testid="navbar">
          {/* === Row 1 === Logo + tagline + language toggle. */}
          <div className="flex items-center justify-between gap-2">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group shrink-0" data-testid="logo-link">
              <span className="relative inline-flex items-center justify-center">
                <span
                  aria-hidden="true"
                  className="absolute inset-0 -m-1.5 rounded-full blur-md opacity-70 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.45) 0%, rgba(0,240,255,0.15) 45%, transparent 70%)' }}
                />
                <CmsImage
                  slotKey="brand.logo"
                  defaultSrc="/xega-logo-new.png"
                  alt="XEGA"
                  className="relative h-7 sm:h-10 md:h-11 w-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] drop-shadow-[0_0_18px_rgba(0,240,255,0.55)] transition-transform duration-500 group-hover:scale-105"
                  style={{ filter: 'contrast(1.08) saturate(1.05) brightness(1.05)', imageRendering: '-webkit-optimize-contrast' }}
                />
              </span>
              <span className="hidden md:block label-mono text-[10px] leading-tight border-l border-white/15 pl-3 !tracking-[0.18em]">
                {lang === "en" ? (<>HIGH SECURITY<br/>TECHNOLOGY</>) : lang === "pt" ? (<>ALTA TECNOLOGIA<br/>EM SEGURANÇA</>) : (<>ALTA TECNOLOGÍA<br/>EN SEGURIDAD</>)}
              </span>
            </Link>

            {/* Desktop inline nav (lg+) */}
            <nav className="hidden lg:flex items-center gap-7 font-tech text-sm text-white/80">
              {NAV_LINKS.map((l, i) => (
                l.hasSubmenu ? (
                  <div
                    key={i}
                    ref={desktopSubmenu.ref}
                    className="relative"
                    onMouseEnter={() => desktopSubmenu.setOpen(true)}
                    onMouseLeave={() => desktopSubmenu.setOpen(false)}
                  >
                    <a
                      href={l.href}
                      data-testid={`nav-link-desktop-${l.testid}`}
                      className="relative flex items-center gap-1 tracking-wide hover:text-[#00F0FF] transition after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-px after:bg-cyan-300 after:transition-all hover:after:w-full"
                    >
                      {l.label}
                      <ChevronDown className={`w-3.5 h-3.5 text-cyan-300/80 transition-transform duration-300 ${desktopSubmenu.open ? 'rotate-180' : ''}`} />
                    </a>
                    {/* Dropdown panel */}
                    <div
                      className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 min-w-[300px] transition-all duration-200 ${desktopSubmenu.open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'}`}
                      data-testid="services-dropdown-desktop"
                    >
                      <div className="chrome-border rounded-xl p-2 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.9)]" style={{ background: 'rgba(8,11,18,0.96)', backdropFilter: 'blur(14px)' }}>
                        <div className="label-mono px-3 pt-2 pb-1 !text-cyan-300 text-[10px]">
                          {pick("CATÁLOGO", "CATALOG", "CATÁLOGO")}
                        </div>
                        <div className="flex flex-col">
                          {SERVICE_ITEMS.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/servicios/${s.slug}`}
                              className={submenuLinkClass}
                              onClick={() => desktopSubmenu.setOpen(false)}
                              data-testid={`submenu-desktop-${s.slug}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-cyan-300/80 shrink-0" />
                              <span className="whitespace-nowrap">{pick(s.es, s.en, s.pt)}</span>
                            </Link>
                          ))}
                        </div>
                        <div className="mx-3 mt-1 mb-2 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                        <Link
                          to="/servicios"
                          onClick={() => desktopSubmenu.setOpen(false)}
                          className="block px-4 py-2 text-[11px] font-tech !tracking-[0.25em] uppercase text-cyan-300/90 hover:text-cyan-200 transition"
                          data-testid="submenu-desktop-all"
                        >
                          {pick("Ver todos", "See all", "Ver todos")} →
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <a
                    key={i}
                    href={l.href}
                    data-testid={`nav-link-desktop-${l.testid}`}
                    className="relative tracking-wide hover:text-[#00F0FF] transition after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-px after:bg-cyan-300 after:transition-all hover:after:w-full"
                  >
                    {l.label}
                  </a>
                )
              ))}
            </nav>

            {/* Compact mobile language pill */}
            <button
              onClick={() => setLang(nextLang)}
              data-testid="lang-toggle"
              aria-label={`Switch to ${nextLang.toUpperCase()}`}
              className="lg:hidden shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full border border-white/15 bg-black/40 text-white/80 hover:text-cyan-300 transition"
            >
              <Globe className="w-3 h-3 text-cyan-300" />
              <span className="font-tech text-[10px] tracking-wider">{lang.toUpperCase()}</span>
            </button>

            {/* Desktop language pill — IDIOMA / ES EN PT */}
            <button
              onClick={() => setLang(nextLang)}
              data-testid="lang-toggle-desktop"
              aria-label={`Switch to ${nextLang.toUpperCase()}`}
              className="hidden lg:inline-flex group relative flex-col items-center gap-1 px-3 pt-1.5 pb-1.5 rounded-2xl chrome-border bg-black/30 hover:border-cyan-400/60 transition shadow-[0_0_18px_-4px_rgba(0,240,255,0.45)] shrink-0"
            >
              <span className="label-mono text-[9px] text-white/55 group-hover:text-white/80 transition !tracking-[0.25em] leading-none">IDIOMA</span>
              <span className="flex items-center gap-1.5">
                <Globe className="w-3 h-3 text-cyan-300 transition-transform duration-700 group-hover:rotate-180" />
                {LANGS.map((code) => (
                  <span
                    key={code}
                    data-testid={`lang-${code}`}
                    onClick={(e)=>{e.stopPropagation(); setLang(code);}}
                    className={`px-1.5 py-0.5 rounded-full text-[9px] font-tech tracking-wider transition ${lang===code ? 'bg-[#00F0FF] text-black shadow-[0_0_10px_rgba(0,240,255,0.55)]' : 'text-white/55 hover:text-white'}`}
                  >{code.toUpperCase()}</span>
                ))}
              </span>
            </button>
          </div>

          {/* === Row 2 (mobile only) === */}
          <div className="lg:hidden" ref={mobileSubmenu.ref}>
            <div className="mt-2 mb-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
            <nav className="flex items-center justify-between gap-1 font-tech text-white/85 px-0.5">
              {NAV_LINKS.map((l, i) => (
                l.hasSubmenu ? (
                  <button
                    key={i}
                    onClick={(e) => { e.preventDefault(); mobileSubmenu.setOpen(!mobileSubmenu.open); }}
                    data-testid={`nav-link-mobile-${l.testid}`}
                    className="relative flex-1 inline-flex items-center justify-center gap-0.5 text-[10.5px] sm:text-xs tracking-wide uppercase whitespace-nowrap py-1 hover:text-[#00F0FF] active:text-[#00F0FF] transition after:content-[''] after:absolute after:-bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-px after:bg-cyan-300 after:transition-all hover:after:w-2/3"
                  >
                    {l.label}
                    <ChevronDown className={`w-3 h-3 text-cyan-300 transition-transform duration-300 ${mobileSubmenu.open ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <a
                    key={i}
                    href={l.href}
                    onClick={() => mobileSubmenu.setOpen(false)}
                    data-testid={`nav-link-mobile-${l.testid}`}
                    className="relative flex-1 text-center text-[10.5px] sm:text-xs tracking-wide uppercase whitespace-nowrap py-1 hover:text-[#00F0FF] active:text-[#00F0FF] transition after:content-[''] after:absolute after:-bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-px after:bg-cyan-300 after:transition-all hover:after:w-2/3"
                  >
                    {l.label}
                  </a>
                )
              ))}
            </nav>

            {/* Mobile Servicios submenu (accordion-style) */}
            <div
              className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${mobileSubmenu.open ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'}`}
              data-testid="services-dropdown-mobile"
            >
              <div className="mt-2 mb-1 h-px bg-gradient-to-r from-transparent via-cyan-300/25 to-transparent" />
              <div className="label-mono !text-cyan-300 text-[9px] px-1 pt-1 pb-1.5">
                {pick("CATÁLOGO", "CATALOG", "CATÁLOGO")}
              </div>
              <div className="flex flex-col gap-0.5 pb-2">
                {SERVICE_ITEMS.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/servicios/${s.slug}`}
                    onClick={() => mobileSubmenu.setOpen(false)}
                    className="flex items-center gap-2 px-2 py-2 text-[12px] font-tech text-white/80 hover:text-cyan-300 rounded-md transition"
                    data-testid={`submenu-mobile-${s.slug}`}
                  >
                    <span className="w-1 h-1 rounded-full bg-cyan-300/60 shrink-0" />
                    {pick(s.es, s.en, s.pt)}
                  </Link>
                ))}
                <Link
                  to="/servicios"
                  onClick={() => mobileSubmenu.setOpen(false)}
                  className="mt-1 px-2 py-1.5 text-[10px] font-tech !tracking-[0.2em] uppercase text-cyan-300/80"
                  data-testid="submenu-mobile-all"
                >
                  {pick("Ver todos", "See all", "Ver todos")} →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
